import { type PlaylistItemData } from './PlaylistItem';

export interface PlaylistState {
	data: PlaylistItemData[];
	error: string | null;
	isLoading: boolean;
}

const mockPlaylistData: PlaylistItemData[] = [
	{
		id: '1',
		title: 'Weekly Team Meeting Summary',
		type: 'Briefing',
		author: 'Sarah Chen',
	},
	{
		id: '2',
		title: 'Q4 Strategic Planning Overview',
		type: 'Narration',
		author: 'Michael Rodriguez-Thompson-Anderson',
	},
	{
		id: '3',
		title: 'User Research Findings also longish title',
		type: 'Briefing',
		author: 'Michael Rodriguez-Thompson-Anderson',
	},
	{
		id: '4',
		title:
			'A page with a very long title that should be truncated if the container is not wide enough',
		type: 'Briefing',
		author: 'James Wilson',
	},
	{
		id: '5',
		title: 'User Research Findings',
		type: 'Briefing',
		author: 'Alex Martinez',
	},
	{
		id: '6',
		title: 'User Research Findings',
		type: 'Briefing',
		author: 'Patricia Johnson',
	},
	{
		id: '7',
		title: 'User Research Findings',
		type: 'Briefing',
		author: 'David Kim',
	},
	{
		id: '8',
		title: 'User Research Findings',
		type: 'Briefing',
		author: 'Jennifer Anderson',
	},
];

// Simple cache for Suspense
interface CacheEntry {
	error?: Error;
	promise?: Promise<PlaylistItemData[]>;
	status: 'pending' | 'fulfilled' | 'rejected';
	value?: PlaylistItemData[];
}

const playlistCache = new Map<string, CacheEntry>();

// Simulate API call
const fetchPlaylistData = async (): Promise<PlaylistItemData[]> => {
	// Simulate network delay
	await new Promise((resolve) => setTimeout(resolve, 1000));

	// Simulate potential error
	// if (Math.random() > 0.8) {
	// 	throw new Error('Failed to load playlist');
	// }

	return mockPlaylistData;
};

/**
 * Custom hook for managing playlist data that works with React Suspense
 * Throws promises while loading and errors on failure
 * Returns data when successful
 * This will be replaced with relay query when BE is ready
 */
export const usePlaylist = (): { playlistItems: PlaylistItemData[] } => {
	const cacheKey = 'playlist';
	let cacheEntry = playlistCache.get(cacheKey);

	if (!cacheEntry) {
		// First time - create cache entry and start fetching
		const promise = fetchPlaylistData();
		cacheEntry = {
			status: 'pending',
			promise,
		};
		playlistCache.set(cacheKey, cacheEntry);

		promise
			.then((data) => {
				cacheEntry!.status = 'fulfilled';
				cacheEntry!.value = data;
			})
			.catch((error) => {
				cacheEntry!.status = 'rejected';
				cacheEntry!.error = error;
			});
	}

	// Handle different cache states
	if (cacheEntry.status === 'pending') {
		// Suspense will catch this thrown promise
		throw cacheEntry.promise;
	}

	if (cacheEntry.status === 'rejected') {
		// ErrorBoundary will catch this thrown error
		throw cacheEntry.error;
	}

	// Return successful data
	return { playlistItems: cacheEntry.value || [] };
};
